/**
 * Customer 360 → «عملیات مالی» → «اصلاح موجودی» (OD-41 G–J, Phase 4 hard requirement). Two steps:
 *   1. request: operation, amount, external reference, internal note → server PREVIEW (read-only);
 *   2. confirm: the server's before → after for available / reserved / total, the target user, the
 *      reference; reason + explicit confirmation + admin step-up → POST with the preview's
 *      before-balances as expected state (a changed balance → STALE_ACTION, re-preview) and one
 *      Idempotency-Key for this intent (a retried click never posts twice).
 * Result: adjustment reference + ledger transaction id. The client never computes a balance.
 * RELEASE / REVERSAL open pre-filled from a hold / an adjustment row (their amount is fixed).
 */
import {
  AmountInput,
  Badge,
  Ltr,
  SelectField,
  TextField,
  formatAmount,
  formatDecimal,
} from '@hopex/ui';
import { useState, type ReactNode } from 'react';
import {
  api,
  ApiFailure,
  errorMessage,
  newIdempotencyKey,
  type Adjustment,
  type AdjustmentOperation,
  type AdjustmentPreview,
} from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { ADJ_OP, assetName } from '../../lib/money-labels.ts';

export interface AdjustmentTarget {
  operation: AdjustmentOperation;
  /** Fixed for RELEASE / REVERSAL (the hold's / adjustment's asset). */
  asset?: string;
  amount?: string;
  reservationId?: string;
  reversesAdjustmentId?: string;
  /** Human reference of the hold / adjustment being released or reversed. */
  targetRef?: string | null;
}

const TONE: Record<AdjustmentOperation, 'positive' | 'danger' | 'caution'> = {
  CREDIT: 'positive',
  DEBIT: 'danger',
  RESERVE: 'caution',
  RELEASE: 'positive',
  REVERSAL: 'caution',
};

const BLOCKED: Record<string, string> = {
  INSUFFICIENT_BALANCE:
    'این عملیات موجودی قابل استفاده یا رزروشده را منفی می‌کند و پذیرفته نمی‌شود.',
};

/** Assets an admin may pick (the server still refuses an inactive one). */
const COMMON_ASSETS = ['IRT', 'USDT', 'BTC', 'ETH', 'TRX', 'BNB'];

/** Exact amount: Toman as usual; a coin with every significant digit (never rounded to 2 places). */
const exact = (v: string, asset: string, withUnit = true) =>
  asset === 'IRT'
    ? formatAmount(v, 'TOMAN', { withUnit })
    : `${formatDecimal(v, { trimTrailingZeros: true })}${withUnit ? ` ${asset}` : ''}`;

function BalanceTable({ p, asset }: { p: AdjustmentPreview; asset: string }) {
  const row = (label: string, k: 'available' | 'reserved' | 'total') => (
    <tr key={k} className="border-t border-line">
      <th scope="row" className="py-2 text-start font-medium text-fg-muted">
        {label}
      </th>
      <td className="py-2 text-end">
        <Ltr className="hx-num">{exact(p.before[k], asset, false)}</Ltr>
      </td>
      <td className="py-2 text-end font-semibold">
        <bdi dir="ltr" className="hx-ltr hx-num font-mono" data-after={k}>
          {exact(p.after[k], asset, false)}
        </bdi>
      </td>
    </tr>
  );
  return (
    <table className="w-full border-collapse text-[13px]" data-adjust-preview-table>
      <thead>
        <tr className="text-[12px] text-fg-subtle">
          <th className="pb-1 text-start font-medium">موجودی ({assetName(asset)})</th>
          <th className="pb-1 text-end font-medium">پیش از عملیات</th>
          <th className="pb-1 text-end font-medium">پس از عملیات</th>
        </tr>
      </thead>
      <tbody>
        {row('قابل استفاده', 'available')}
        {row('رزروشده', 'reserved')}
        {row('کل', 'total')}
      </tbody>
    </table>
  );
}

export function AdjustmentDialog({
  userId,
  userLabel,
  initial,
  assets = [],
  onClose,
  onDone,
}: {
  userId: string;
  userLabel: string;
  initial: AdjustmentTarget | null;
  /** The customer's held assets, offered next to the common ones. */
  assets?: string[];
  onClose: () => void;
  onDone: (a: Adjustment) => void;
}) {
  const fixed = initial?.operation === 'RELEASE' || initial?.operation === 'REVERSAL';
  const [operation, setOperation] = useState<AdjustmentOperation>(initial?.operation ?? 'CREDIT');
  const [asset, setAsset] = useState(initial?.asset ?? 'IRT');
  const [amount, setAmount] = useState(initial?.amount ?? '');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<AdjustmentPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [key, setKey] = useState(newIdempotencyKey);
  const [result, setResult] = useState<Adjustment | null>(null);

  const request = {
    operation,
    asset,
    amount,
    ...(initial?.reservationId ? { reservationId: initial.reservationId } : {}),
    ...(initial?.reversesAdjustmentId
      ? { reversesAdjustmentId: initial.reversesAdjustmentId }
      : {}),
  };
  // Toman: one decimal (Rial). A coin: up to 18 digits here; the server enforces the asset's own scale.
  const ready =
    (asset === 'IRT' ? /^\d+(\.\d)?$/ : /^\d+(\.\d{1,18})?$/).test(amount) &&
    !/^0+(\.0+)?$/.test(amount);
  const assetOptions = [...new Set([...COMMON_ASSETS, ...assets])].map((a) => ({
    value: a,
    label: assetName(a),
  }));

  async function loadPreview() {
    setLoading(true);
    setPreviewError(null);
    try {
      const p = await api.post<AdjustmentPreview>(`/users/${userId}/adjustments/preview`, request);
      setPreview(p);
      setConfirmed(false);
      setKey(newIdempotencyKey()); // a new preview is a new intent
    } catch (e) {
      setPreviewError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  if (result)
    return (
      <CommandDialog
        title="اصلاح موجودی ثبت شد"
        reasonRequired={false}
        confirmLabel="بستن"
        run={() => Promise.resolve()}
        onClose={() => onDone(result)}
        onDone={() => onDone(result)}
        data-adjust-result
      >
        <Facts
          rows={[
            ['عملیات', ADJ_OP[result.operation] ?? result.operation],
            [
              'مبلغ',
              <Ltr key="a" className="font-semibold">
                {exact(result.amount, asset)}
              </Ltr>,
            ],
            [
              'کد اصلاحیه',
              <bdi key="r" dir="ltr" className="hx-ltr font-mono" data-adjustment-ref>
                {result.ref}
              </bdi>,
            ],
            [
              'شناسه‌ی سند دفتر کل',
              <bdi key="t" dir="ltr" className="hx-ltr font-mono text-[12px]" data-ledger-tx>
                {result.transactionId}
              </bdi>,
            ],
            [
              'موجودی پس از عملیات',
              <Ltr key="b">
                {exact(result.after.available, asset)} قابل استفاده ·{' '}
                {exact(result.after.reserved, asset)} رزرو
              </Ltr>,
            ],
          ]}
        />
      </CommandDialog>
    );

  if (!preview)
    return (
      <CommandDialog
        title="عملیات مالی — اصلاح موجودی"
        reasonRequired={false}
        confirmLabel="پیش‌نمایش اثر"
        tone="primary"
        canSubmit={ready && !loading && reference.trim().length >= 3}
        run={loadPreview}
        onClose={onClose}
        onDone={() => {}}
        data-adjust-form
      >
        <Facts
          rows={[
            [
              'کاربر',
              <span key="u" className="font-semibold">
                {userLabel}
              </span>,
            ],
            ...(fixed ? ([['دارایی', assetName(asset)]] as [string, ReactNode][]) : []),
            ...(initial?.targetRef
              ? ([
                  [
                    operation === 'RELEASE' ? 'مسدودی' : 'اصلاحیه‌ی اصلی',
                    <Ltr key="t" className="font-mono">
                      {initial.targetRef}
                    </Ltr>,
                  ],
                ] as [string, ReactNode][])
              : []),
          ]}
        />
        {fixed ? (
          <p className="m-0 text-[13px]">
            عملیات: <Badge tone="accent">{ADJ_OP[operation]}</Badge>
          </p>
        ) : (
          <SelectField
            label="نوع عملیات"
            value={operation}
            onChange={(v) => setOperation(v as AdjustmentOperation)}
            options={(['CREDIT', 'DEBIT', 'RESERVE'] as const).map((o) => ({
              value: o,
              label: ADJ_OP[o] as string,
            }))}
          />
        )}
        {!fixed && (
          <SelectField
            label="دارایی"
            value={asset}
            onChange={(v) => (setAsset(v), setAmount(''))}
            options={assetOptions}
            data-adjust-asset
          />
        )}
        {fixed ? (
          <p className="m-0 text-[13px]" data-adjust-amount-fixed>
            مبلغ: <Ltr className="font-bold">{exact(amount, asset)}</Ltr>
            <span className="block text-[12px] text-fg-subtle">
              برابر مبلغ اصلی است و قابل تغییر نیست.
            </span>
          </p>
        ) : (
          <AmountInput
            label={`مقدار (${assetName(asset)})`}
            value={amount}
            onChange={setAmount}
            unit={assetName(asset)}
            scale={asset === 'IRT' ? 1 : 18}
            description={
              asset === 'IRT'
                ? 'تا یک رقم اعشار (ریال). کاهش و مسدودسازی فقط از موجودی قابل استفاده.'
                : 'مقدار دقیق؛ دقت اعشار بیش از حد این دارایی را سرور رد می‌کند. کاهش و مسدودسازی فقط از موجودی قابل استفاده.'
            }
            data-adjust-amount
          />
        )}
        <TextField
          label="شماره‌ی مرجع (الزامی)"
          value={reference}
          onChange={setReference}
          ltr
          description="شماره‌ی پرونده، تیکت یا سند پشتیبان این اصلاح (۳ تا ۶۴ نویسه)."
          data-adjust-reference
        />
        <TextField
          label="یادداشت داخلی (اختیاری)"
          value={note}
          onChange={setNote}
          description="فقط برای مدیران؛ به کاربر نمایش داده نمی‌شود."
        />
        {previewError && (
          <p role="alert" className="m-0 text-[13px] text-negative">
            {previewError}
          </p>
        )}
      </CommandDialog>
    );

  return (
    <CommandDialog
      title={`تأیید ${ADJ_OP[operation] ?? operation}`}
      confirmLabel="تأیید و ثبت در دفتر کل"
      tone={TONE[operation] === 'positive' ? 'positive' : TONE[operation]}
      canSubmit={confirmed && preview.blocked === null}
      run={async (reason) => {
        try {
          setResult(
            await api.post<Adjustment>(
              `/users/${userId}/adjustments`,
              {
                ...request,
                reason,
                ...(note.trim() ? { internalNote: note.trim() } : {}),
                externalReference: reference.trim(),
                expectedAvailable: preview.before.available,
                expectedReserved: preview.before.reserved,
                confirm: true,
              },
              { 'idempotency-key': key },
            ),
          );
        } catch (e) {
          if (e instanceof ApiFailure && e.code === 'STALE_ACTION') {
            setPreview(null);
            setPreviewError(
              'موجودی کاربر از زمان پیش‌نمایش تغییر کرده است. دوباره پیش‌نمایش بگیرید.',
            );
            return;
          }
          throw e;
        }
      }}
      onClose={onClose}
      onDone={() => {}}
      data-adjust-confirm={operation}
    >
      <Facts
        rows={[
          [
            'کاربر',
            <span key="u" className="font-semibold">
              {userLabel}
            </span>,
          ],
          ['عملیات', ADJ_OP[operation] ?? operation],
          ['دارایی', <span key="as" className="font-bold">{assetName(asset)}</span>],
          [
            'مبلغ',
            <Ltr key="a" className="font-bold">
              {exact(preview.amount, asset)}
            </Ltr>,
          ],
          [
            'مرجع',
            <Ltr key="r" className="font-mono">
              {reference.trim()}
            </Ltr>,
          ],
          ...(preview.target?.ref
            ? ([
                [
                  'هدف',
                  <Ltr key="t" className="font-mono">
                    {preview.target.ref}
                  </Ltr>,
                ],
              ] as [string, ReactNode][])
            : []),
        ]}
      />
      <BalanceTable p={preview} asset={asset} />
      {preview.blocked ? (
        <p
          role="alert"
          className="m-0 rounded-md border border-negative/40 bg-negative-tint px-3 py-2 text-[13px] text-negative"
          data-adjust-blocked
        >
          {BLOCKED[preview.blocked] ?? 'این عملیات پذیرفته نمی‌شود.'}
        </p>
      ) : (
        <label className="flex items-start gap-2 text-[13px] leading-6">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1.5"
            data-adjust-ack
          />
          اثر این عملیات را بررسی کردم. یک سند جدید و غیرقابل‌ویرایش در دفتر کل ثبت می‌شود؛ اصلاح
          بعدی فقط با سند جبرانی ممکن است.
        </label>
      )}
    </CommandDialog>
  );
}
